/**
 * Configuration for Admin → Field Settings.
 *
 * WHAT THE SCREEN IS FOR
 * Labels, display formats and field visibility are metadata rows, not code.
 * Changing one used to mean an engineer writing SQL, a migration applied by
 * hand and a release; this screen makes it an edit. See
 * Warwick-Investment-Group/wig-broker-deals#189.
 *
 * WHY THIS NEEDS CONFIGURATION AT ALL
 * Unlike App Support, the metadata table is NOT shared. It is coupled to each
 * app's own schema — its lookups name that app's tables, its `required` flag
 * is derived from that app's request models, and `editable_for_me` is computed
 * from that app's roles. So the storage stays per-app and only the CONTRACT is
 * shared:
 *
 *   GET  {base}/metadata/tables        the tables that have metadata
 *   GET  {base}/metadata?table_name=   the rows for one table
 *   PUT  {base}/metadata/{id}          edit one row
 *   GET  {base}/metadata/audit         who changed what
 *
 *   VITE_APP_API_BASE  the app's own de-hub module path, e.g. `/broker_deals`.
 *                      The screen must never name a table or a module itself —
 *                      that is what keeps it in the template rather than
 *                      needing a copy per app.
 *
 *   VITE_ADMIN_ROLE    reused from App Support: the same admin administers
 *                      both, and a second role name to keep in step would be a
 *                      second thing to get wrong.
 *
 * The server is the real boundary. This decides what UI to draw; de-hub's
 * `@requires_roles([ADMIN])` decides what is allowed.
 */

import { APP_KEY, API_BASE as APP_SUPPORT_BASE } from "./appSupport";

/** The app's own de-hub module path. Empty when unset — see IS_UNCONFIGURED. */
export const APP_API_BASE = (
  import.meta.env?.VITE_APP_API_BASE || ""
).trim().replace(/\/+$/, "");

/**
 * True when `VITE_APP_API_BASE` was never set.
 *
 * Surfaced in the UI rather than left to fail as a 404, because "the screen is
 * empty" is a baffling symptom for a missing environment variable — the same
 * reasoning as `appSupport.IS_UNCONFIGURED`, and the same mistake this
 * codebase has already made once with a silent database fallback.
 */
export const IS_UNCONFIGURED = APP_API_BASE === "";

/**
 * The metadata tables this screen can edit.
 *
 * There is more than one because the app's own tables and the SHARED
 * App Support tables (the feedback queue, announcements) live in different
 * de-hub modules and different databases. Both implement the same four-endpoint
 * contract, so the screen talks to them identically — it just has to be told
 * which base, and whether that base scopes rows by app.
 *
 * `app` is the difference. The app's own metadata table belongs to one app by
 * construction and takes no such parameter. App Support's is shared by every
 * WIG front end, so every call carries `?app=` or you would be editing another
 * app's labels.
 *
 * Nothing here names a table — the server's `/metadata/tables` still decides
 * what is editable, which is what keeps this file app-agnostic.
 */
export const METADATA_SOURCES = [
  {
    key: "app",
    label: "This app",
    base: APP_API_BASE,
    app: null,
  },
  {
    key: "app_support",
    label: "App Support (shared)",
    base: APP_SUPPORT_BASE,
    app: APP_KEY,
  },
];

/** The source a fresh visit starts on. */
export const DEFAULT_SOURCE_KEY = "app";

/** Look one up by key, falling back to the default rather than throwing. */
export const sourceByKey = (key) =>
  METADATA_SOURCES.find((s) => s.key === key) || METADATA_SOURCES[0];

/**
 * Properties this screen offers for editing.
 *
 * Mirrors de-hub's `UI_METADATA_EDITABLE_PROPERTIES`; anything else is
 * rejected by the server with a 422 naming it. Kept here so the form can be
 * built from one list rather than nine hand-written fields.
 *
 * `editable` carries a warning because it is UI-ONLY — the enforcing boundary
 * is the server's own role checks. Without saying so, a checkbox labelled
 * "Editable" reads as a permission control, and an admin could reasonably
 * believe unticking it stops someone writing the field. It does not.
 */
export const EDITABLE_PROPERTIES = [
  {
    name: "label",
    label: "Label",
    type: "text",
    help: "What users see. Also the column header in the upload workbook.",
  },
  {
    name: "format",
    label: "Number format",
    type: "select",
    help: "How a number is displayed. Leave blank for a plain number.",
    options: [
      { value: "", label: "Plain number" },
      { value: "money.ledger", label: "Money — whole dollars ($1,234,568)" },
      { value: "money.exact", label: "Money — to the cent ($1,234,567.89)" },
      { value: "money.rate", label: "Money — per-unit rate ($1,187.50)" },
      { value: "acres", label: "Acreage (1,234.567891)" },
      { value: "interest", label: "Interest — 8 decimals (0.80123450)" },
    ],
  },
  { name: "category", label: "Section", type: "text",
    help: "Groups fields into sections on the detail screens." },
  { name: "subcategory", label: "Sub-section", type: "text" },
  { name: "definition", label: "Description", type: "textarea",
    help: "Shown as help text. Nobody reads a field they cannot interpret." },
  { name: "ord", label: "Order", type: "number",
    help: "Position within its section, and column order in the grid." },
  { name: "width", label: "Column width", type: "number" },
  { name: "hide", label: "Hidden", type: "boolean",
    help: "Hides the field from grids and forms." },
  {
    name: "filterable",
    label: "Column filter",
    type: "select",
    help:
      "Whether the grid column offers a filter dropdown. Leave on Default " +
      "unless the list is unhelpful — a column where every row is unique " +
      "(a filename, a check number) lists the data back instead of " +
      "narrowing it.",
    // THREE values, not a switch. Default is not the same as No: it means
    // nobody has expressed a view, so the grid's own judgement applies and
    // surrogate keys stay unfiltered without anyone saying so. A switch would
    // force an opinion onto every field the moment it was added.
    options: [
      { value: null, label: "Default" },
      { value: true, label: "Always filterable" },
      { value: false, label: "Never filterable" },
    ],
  },
  {
    name: "editable",
    label: "Editable",
    type: "boolean",
    help:
      "Draws the field as an input rather than read-only. This is a display " +
      "setting, NOT a permission — who may actually write the field is " +
      "decided by their role on the server.",
  },
  {
    name: "bulk_editable",
    label: "Bulk editable",
    type: "boolean",
    help:
      "Offers the field in Bulk Edit on the deals list (dropdowns and dates " +
      "only). Like Editable, NOT a permission: each user still sees only the " +
      "fields their role may edit on a single deal.",
  },
];

export default {
  APP_API_BASE,
  IS_UNCONFIGURED,
  EDITABLE_PROPERTIES,
  METADATA_SOURCES,
  DEFAULT_SOURCE_KEY,
  sourceByKey,
};
